import React, { FunctionComponent, useCallback, ReactText, useMemo } from 'react';
import { useStore } from 'effector-react';
import gamesRequest, { approve, getList } from '@stores/gamesRequest/gamesRequest';
import selectedGames, {
  addSelectedGames,
  clearSelectedGames,
} from '@stores/gamesRequest/selectedGames';
import { ColumnsType, TablePaginationConfig } from 'antd/lib/table';
import useScrollTopPage from '@hooks/useScrollTopPage';
import useTranslate from '@hooks/useTranslate';
import componentContent from '../GamesPage.module.json';
import { SortOrder } from 'antd/lib/table/interface';
import { Button, Spin, Table, Affix, Badge } from 'antd';
import { openModal } from '@stores/modals';
import { pageSizeOptions } from '@constants/pagination';
import { IGameRequest } from '@interfaces/IGameRequest';
import GameCardCell from '@components/GameCard/GameCardCell';
import {
  CheckCircleTwoTone,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleTwoTone,
  IssuesCloseOutlined,
} from '@ant-design/icons';
import UserShortCard from '@components/UserShortCard';
import css from './GamesRequestList.module.less';
import useGuards from '@hooks/useGuards';

const GamesRequestList: FunctionComponent<unknown> = () => {
  const { list, preloaders, pagination, filters: currentFilterParams } = useStore(gamesRequest);
  const selectedRows = useStore(selectedGames);
  const { allowChange } = useGuards();

  const content = useTranslate(componentContent);

  const onChange = useCallback((pagination: TablePaginationConfig, filters: any, sorter: any) => {
    localStorage.setItem('gamesRequestPageSize', `${pagination.pageSize}`);
    getList({
      pagination,
      sorter,
    });
  }, []);

  useScrollTopPage(pagination);

  const [currentSortedColumnKey, sortDirection] = (currentFilterParams.sort || '').split(',');

  const onSelectChange = useCallback(
    (selectedRowKeys: ReactText[], selectedRow: IGameRequest[]) => {
      addSelectedGames(selectedRow);
    },
    [],
  );
  const selectedRowKeys = useMemo(() => {
    return selectedRows.map((game) => game.id);
  }, [selectedRows]);

  const reject = useCallback((games: IGameRequest[]) => {
    openModal({
      data: { games, type: 'REJECT' },
      name: 'gameRequest',
    });
  }, []);

  const rejectAll = useCallback(() => {
    reject(selectedRows);
  }, [reject, selectedRows]);

  const gameExist = useCallback((games: IGameRequest[]) => {
    openModal({
      data: { games: games, type: 'GAME_EXIST' },
      name: 'gameRequest',
    });
  }, []);

  const gameExistAll = useCallback(() => {
    gameExist(selectedRows);
  }, [gameExist, selectedRows]);

  const resetSelectedAll = useCallback(() => {
    clearSelectedGames();
  }, []);

  const columns: ColumnsType<IGameRequest> = [
    {
      key: 'id',
      dataIndex: 'id',
      title: content.id,
      width: 56,
    },
    {
      key: 'approved',
      title: content.status,
      width: 64,
      render: (_, game) =>
        game.approved ? (
          <CheckCircleTwoTone twoToneColor='#52c41a' />
        ) : (
          <ExclamationCircleTwoTone twoToneColor='#fa8c16' />
        ),
    },
    {
      key: 'name',
      title: content.name,
      render: (_, game) => (
        <GameCardCell
          text={game.name}
          urlImg={game?.image?.url || ''}
          isEmpty={!game}
        />
      ),
      sorter: true,
      sortOrder:
        currentSortedColumnKey === 'name' ? (`${sortDirection}end` as SortOrder) : undefined,
      showSorterTooltip: false,
    },
    {
      key: 'appleStoreUrl',
      title: content.url,
      render: (_, game) => (
        <GameCardCell
          text={game?.appleStoreUrlOrExternal || game?.googlePlayUrlOrExternal}
          urlImg={''}
          isEmpty={!game}
          isLink
        />
      ),
      sorter: true,
      sortOrder:
        currentSortedColumnKey === 'appleStoreUrl'
          ? (`${sortDirection}end` as SortOrder)
          : undefined,
      showSorterTooltip: false,
    },
    {
      key: 'author',
      title: content.author,
      render: (_, game) => (game.author ? <UserShortCard user={game.author} /> : null),
    },
    {
      key: 'post',
      title: content.post,
      render: (_, game) =>
        game.post ? (
          <a
            className='clickable'
            href={`/post-list/${game.post.id}`}
          >
            {game.post.id}
          </a>
        ) : null,
    },
    {
      key: 'approve',
      width: 64,
      render: (_, game) => {
        return allowChange ? (
          <Button
            onClick={() => {
              openModal({
                name: 'game',
                data: {
                  ...game,
                  suggestId: game.id,
                  isRequest: true,
                  shouldApproveAfterSubmit: true,
                },
              });
            }}
            icon={<CheckCircleOutlined />}
            shape='circle'
            title={content.approve}
            loading={!!preloaders.approve}
          />
        ) : null;
      },
    },
    {
      key: 'gameExist',
      width: 64,
      render: (_, game) => {
        return allowChange ? (
          <Button
            onClick={() => {
              gameExist([game]);
            }}
            icon={<IssuesCloseOutlined />}
            shape='circle'
            title={content.game_exist}
            loading={!!preloaders.gameExist}
          />
        ) : null;
      },
    },
    {
      key: 'reject',
      width: 64,
      render: (_, game) => {
        return allowChange ? (
          <Button
            onClick={() => {
              reject([game]);
            }}
            icon={<CloseCircleOutlined />}
            shape='circle'
            title={content.reject}
            loading={!!preloaders.reject}
          />
        ) : null;
      },
    },
  ];

  return (
    <>
      {allowChange ? (
        <Affix offsetTop={0}>
          <div className={css.action}>
            <Badge count={selectedRows.length}>
              <Button
                htmlType='button'
                type='primary'
                onClick={rejectAll}
                disabled={!selectedRows.length}
                loading={!!preloaders.getList}
                icon={<CloseCircleOutlined />}
              >
                {content.reject_selected}
              </Button>
            </Badge>
            <Badge count={selectedRows.length}>
              <Button
                htmlType='button'
                type='primary'
                onClick={gameExistAll}
                disabled={!selectedRows.length}
                loading={!!preloaders.getList}
                icon={<IssuesCloseOutlined />}
                className={css.action__button}
              >
                {content.game_exist_selected}
              </Button>
            </Badge>
            <Button
              htmlType='button'
              type='link'
              onClick={resetSelectedAll}
              disabled={!selectedRows.length}
              loading={!!preloaders.getList}
              className={css.action__button}
            >
              {content.reset_selected}
            </Button>
          </div>
        </Affix>
      ) : null}
      <Spin spinning={!!preloaders.getList}>
        <Table
          rowKey='id'
          dataSource={list}
          columns={columns}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            pageSizeOptions,
            position: ['topRight', 'bottomRight'],
          }}
          onChange={onChange}
          rowSelection={{
            onChange: onSelectChange,
            preserveSelectedRowKeys: true,
            selectedRowKeys: selectedRowKeys,
          }}
        />
      </Spin>
    </>
  );
};

export default GamesRequestList;
